export interface LatLngPoint {
  lat: number;
  lng: number;
}

export interface BuildingGeometry {
  points: LatLngPoint[];
  center: LatLngPoint;
  bounds: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  };
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toLatLngPoint = (value: unknown): LatLngPoint | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
   
  const source = value as Record<string, unknown>;
  const lat =
    toNumber(source.lat) ??
    toNumber(source.latitude) ??
    toNumber(source.y);
  const lng =
    toNumber(source.lng) ??
    toNumber(source.longitude) ??
    toNumber(source.x);

  if (lat === null || lng === null) {
    return null;
  }

  return { lat, lng };
};

const extractPointsFromValue = (value: unknown): LatLngPoint[] | null => {
  if (!value) {
    return null;
  }

  if (Array.isArray(value)) {
    const points = value
      .map(item => toLatLngPoint(item))
      .filter((point): point is LatLngPoint => point !== null);
    return points.length > 0 ? points : null;
  }

  if (typeof value === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const source = value as Record<string, any>;
    if (Array.isArray(source.points)) {
      return extractPointsFromValue(source.points);
    }
    if (Array.isArray(source.path)) {
      return extractPointsFromValue(source.path);
    }
    if (Array.isArray(source.coords)) {
      return extractPointsFromValue(source.coords);
    }
    if (Array.isArray(source.coordinate)) {
      return extractPointsFromValue(source.coordinate);
    }
  }

  return null;
};

const extractBoundsFromValue = (value: unknown): { sw: LatLngPoint; ne: LatLngPoint } | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const source = value as Record<string, any>;

  const sw =
    toLatLngPoint(source.sw) ??
    toLatLngPoint(source.sPoint);
  const ne =
    toLatLngPoint(source.ne) ??
    toLatLngPoint(source.ePoint);

  if (!sw || !ne) {
    return null;
  }

  return { sw, ne };
};

const sanitizeCoordinateInput = (input: string): string => {
  let trimmed = input.trim();
  if (trimmed === '') {
    return trimmed;
  }

  trimmed = trimmed.replace(/^(다각형|사각형)\s*/i, '');

  const firstBracket = trimmed.indexOf('[');
  const firstBrace = trimmed.indexOf('{');
  if (firstBracket !== -1 && (firstBrace === -1 || firstBracket < firstBrace)) {
    trimmed = trimmed.slice(firstBracket);
  } else if (firstBrace !== -1) {
    trimmed = trimmed.slice(firstBrace);
  }

  return trimmed;
};

const computeGeometryFromPoints = (points: LatLngPoint[]): BuildingGeometry | null => {
  if (points.length === 0) {
    return null;
  }

  let minLat = points[0]?.lat ?? 0;
  let maxLat = points[0]?.lat ?? 0;
  let minLng = points[0]?.lng ?? 0;
  let maxLng = points[0]?.lng ?? 0;

  points.forEach(point => {
    if (point.lat < minLat) minLat = point.lat;
    if (point.lat > maxLat) maxLat = point.lat;
    if (point.lng < minLng) minLng = point.lng;
    if (point.lng > maxLng) maxLng = point.lng;
  });

  const center = {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2
  };

  return {
    points,
    center,
    bounds: { minLat, maxLat, minLng, maxLng }
  };
};

const createRectanglePoints = (sw: LatLngPoint, ne: LatLngPoint): LatLngPoint[] => [
  { lat: sw.lat, lng: sw.lng },
  { lat: sw.lat, lng: ne.lng },
  { lat: ne.lat, lng: ne.lng },
  { lat: ne.lat, lng: sw.lng }
];

export const parseBuildingGeometry = (
  shapeType: string | null | undefined,
  raw: unknown
): BuildingGeometry | null => {
  if (raw === null || raw === undefined) {
    return null;
  }

  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    const sanitized = sanitizeCoordinateInput(raw);
    if (sanitized === '') {
      return null;
    }
    try {
      parsed = JSON.parse(sanitized);
    } catch (error) {
      return null;
    }
  }

  const normalizedShape = (shapeType ?? '').toLowerCase();

  let points: LatLngPoint[] | null = null;

  if (Array.isArray(parsed)) {
    points = extractPointsFromValue(parsed);

    if (!points) {
      // Check if array entries contain nested points (e.g., [{ type: 'polygon', points: [...] }])
      for (const entry of parsed) {
        points = extractPointsFromValue(entry);
        if (points && points.length > 0) {
          break;
        }
      }
    }
  } else if (typeof parsed === 'object') {
    points = extractPointsFromValue(parsed);

    if (!points && parsed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const objectValue = parsed as Record<string, any>;
      if (Array.isArray(objectValue?.polygon)) {
        points =
          extractPointsFromValue(objectValue.polygon[0]?.points ?? objectValue.polygon) ??
          extractPointsFromValue(objectValue.polygon);
      }
      if (!points && Array.isArray(objectValue?.coordinates)) {
        points = extractPointsFromValue(objectValue.coordinates);
      }
    }
  }

  if ((!points || points.length === 0) && typeof parsed === 'object') {
    const bounds = extractBoundsFromValue(parsed);
    if (bounds) {
      points = createRectanglePoints(bounds.sw, bounds.ne);
    }
  }

  // If rectangle coordinates were stored as points, ensure we have rectangular bounds
  if (
    (!points || points.length === 0) &&
    normalizedShape === 'rectangle' &&
    Array.isArray(parsed)
  ) {
    const latValues = parsed.map(item => toLatLngPoint(item)).filter(Boolean) as LatLngPoint[];
    if (latValues.length >= 2) {
      const lats = latValues.map(point => point.lat);
      const lngs = latValues.map(point => point.lng);
      const sw = { lat: Math.min(...lats), lng: Math.min(...lngs) };
      const ne = { lat: Math.max(...lats), lng: Math.max(...lngs) };
      points = createRectanglePoints(sw, ne);
    }
  }

  if (!points || points.length === 0) {
    return null;
  }

  return computeGeometryFromPoints(points);
};

export const trimCoordinateInput = (input: string): string => input.trim();
